/**
 * Session state.
 *
 * Holds the unlocked data key, the settings, and the in-memory caches the
 * suggestion engine reads. The data key lives here and nowhere else: it is
 * never written to storage, so closing the tab locks the vault.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { AuditContext } from '@/db/audit';
import { accountsDb, closeAccountDb, openAccountDb } from '@/db/schema';
import {
  addressRepo,
  getAppSettings,
  getCompanySettings,
  passengerRepo,
  recentTrips,
  vehicleRepo,
} from '@/db/repositories';
import { DestinationIndex } from '@/suggest';
import {
  DEFAULT_APP_SETTINGS,
  DEFAULT_COMPANY_SETTINGS,
  type Address,
  type AppSettings,
  type CompanySettings,
  type Passenger,
  type Vehicle,
} from '@/domain/types';
import type { AccountRecord } from '@/crypto/vault';
import { unlockWithPassword, unlockWithRecoveryCode } from '@/crypto/vault';
import { appendAudit } from '@/db/audit';
import { useLanguage, useT } from '@/i18n';

/** Trips loaded into the destination index at unlock. */
const DESTINATION_INDEX_TRIP_LIMIT = 5000;

export interface SessionValue {
  account: AccountRecord;
  dataKey: CryptoKey;
  audit: AuditContext;

  company: CompanySettings;
  app: AppSettings;

  passengers: Passenger[];
  addresses: Address[];
  vehicles: Vehicle[];
  destinations: DestinationIndex;

  /** Re-read settings and caches after a mutation. */
  refresh: () => Promise<void>;
  refreshMasterData: () => Promise<void>;
  lock: () => void;
}

const SessionContext = createContext<SessionValue | null>(null);

export function useSession(): SessionValue {
  const value = useContext(SessionContext);
  if (!value) throw new Error('useSession muss innerhalb von <SessionProvider> verwendet werden.');
  return value;
}

/** Optional variant for components that render both locked and unlocked. */
export function useMaybeSession(): SessionValue | null {
  return useContext(SessionContext);
}

interface UnlockedState {
  account: AccountRecord;
  dataKey: CryptoKey;
}

export interface SessionProviderProps {
  unlocked: UnlockedState;
  onLock: () => void;
  children: ReactNode;
}

export function SessionProvider({ unlocked, onLock, children }: SessionProviderProps) {
  const { dataKey } = unlocked;
  const t = useT();
  const { language, setLanguage } = useLanguage();

  // The account row can be edited in settings, so it is state rather than a
  // straight read of the prop.
  const [account, setAccount] = useState<AccountRecord>(unlocked.account);
  const [company, setCompany] = useState<CompanySettings>(DEFAULT_COMPANY_SETTINGS);
  const [app, setApp] = useState<AppSettings>(DEFAULT_APP_SETTINGS);
  const [passengers, setPassengers] = useState<Passenger[]>([]);
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [destinations, setDestinations] = useState<DestinationIndex>(() => new DestinationIndex());
  const [ready, setReady] = useState(false);

  const accountId = account.id;

  const audit = useMemo<AuditContext>(
    () => ({ dataKey, driverId: accountId }),
    [dataKey, accountId],
  );

  const refreshSettings = useCallback(async () => {
    const [nextCompany, nextApp, nextAccount] = await Promise.all([
      getCompanySettings(dataKey),
      getAppSettings(dataKey),
      accountsDb.accounts.get(accountId),
    ]);
    setCompany(nextCompany);
    setApp(nextApp);
    if (nextAccount) setAccount(nextAccount);
  }, [dataKey, accountId]);

  const refreshMasterData = useCallback(async () => {
    const [nextPassengers, nextAddresses, nextVehicles, trips] = await Promise.all([
      passengerRepo.all(dataKey),
      addressRepo.all(dataKey),
      vehicleRepo.all(dataKey),
      recentTrips(dataKey, DESTINATION_INDEX_TRIP_LIMIT),
    ]);
    setPassengers(nextPassengers);
    setAddresses(nextAddresses);
    setVehicles(nextVehicles);
    setDestinations(DestinationIndex.build(trips));
  }, [dataKey]);

  const refresh = useCallback(async () => {
    await Promise.all([refreshSettings(), refreshMasterData()]);
  }, [refreshSettings, refreshMasterData]);

  // Locking drops the key; it should drop the open database with it, so the
  // next driver to sign in cannot inherit a handle to someone else's books.
  const lock = useCallback(() => {
    closeAccountDb();
    onLock();
  }, [onLock]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      await refresh();
      if (!cancelled) setReady(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [refresh]);

  // Settings win over the local copy at unlock, so restoring a backup on a new
  // device brings the driver's language with it. Only at unlock: after that the
  // switch in settings is authoritative, and re-running this would fight it
  // during the moment between the switch and the write.
  const languageApplied = useRef(false);
  useEffect(() => {
    if (!ready || languageApplied.current) return;
    languageApplied.current = true;
    if (app.language !== language) setLanguage(app.language);
  }, [ready, app.language, language, setLanguage]);

  const value = useMemo<SessionValue | null>(
    () =>
      ready
        ? {
            account,
            dataKey,
            audit,
            company,
            app,
            passengers,
            addresses,
            vehicles,
            destinations,
            refresh,
            refreshMasterData,
            lock,
          }
        : null,
    [
      ready,
      account,
      dataKey,
      audit,
      company,
      app,
      passengers,
      addresses,
      vehicles,
      destinations,
      refresh,
      refreshMasterData,
      lock,
    ],
  );

  if (!value) {
    return (
      <div className="flex min-h-dvh items-center justify-center">
        <p className="text-muted text-sm">{t.shell.decrypting}</p>
      </div>
    );
  }

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

// ---------------------------------------------------------------------------
// Unlock plumbing
// ---------------------------------------------------------------------------

export async function listAccounts(): Promise<AccountRecord[]> {
  return accountsDb.accounts.toArray();
}

export async function hasAnyAccount(): Promise<boolean> {
  return (await accountsDb.accounts.count()) > 0;
}

export async function unlockAccount(
  username: string,
  password: string,
): Promise<UnlockedState> {
  const account = await accountsDb.accounts
    .where('username')
    .equals(username.trim().toLowerCase())
    .first();
  if (!account) {
    // Same message as a wrong password: whether a username exists is not
    // something a lock screen should confirm.
    const { WrongPasswordError } = await import('@/crypto/vault');
    throw new WrongPasswordError();
  }
  const vault = await unlockWithPassword(account, password);
  // Every later query goes through `db`, so the books have to be swapped in
  // before anything — including the login audit entry below — is written.
  openAccountDb(account.dbName);
  await appendAudit(
    { dataKey: vault.dataKey, driverId: account.id },
    {
      action: 'login',
      entityType: 'system',
      entityId: account.id,
      entityLabel: `Anmeldung ${account.displayName}`,
      changes: [],
    },
  );
  return { account, dataKey: vault.dataKey };
}

export async function unlockAccountWithRecovery(
  username: string,
  recoveryCode: string,
): Promise<UnlockedState> {
  const account = await accountsDb.accounts
    .where('username')
    .equals(username.trim().toLowerCase())
    .first();
  if (!account) {
    const { WrongRecoveryCodeError } = await import('@/crypto/vault');
    throw new WrongRecoveryCodeError();
  }
  const vault = await unlockWithRecoveryCode(account, recoveryCode);
  openAccountDb(account.dbName);
  return { account, dataKey: vault.dataKey };
}

/**
 * Auto-lock after inactivity.
 *
 * A chauffeur leaves the phone in the car. Ten minutes of no interaction and
 * the key is dropped from memory, which is the only protection that survives
 * the device being handed to someone else.
 */
export function useIdleLock(onLock: () => void, timeoutMinutes = 10): void {
  const timer = useRef<number | undefined>(undefined);

  useEffect(() => {
    const reset = () => {
      window.clearTimeout(timer.current);
      timer.current = window.setTimeout(onLock, timeoutMinutes * 60_000);
    };

    const windowEvents: Array<keyof WindowEventMap> = ['pointerdown', 'keydown', 'focus'];
    for (const event of windowEvents) window.addEventListener(event, reset);
    // Returning from the background counts as activity; it fires on document.
    document.addEventListener('visibilitychange', reset);
    reset();

    return () => {
      window.clearTimeout(timer.current);
      for (const event of windowEvents) window.removeEventListener(event, reset);
      document.removeEventListener('visibilitychange', reset);
    };
  }, [onLock, timeoutMinutes]);
}
